import { Button } from '@verlo/shared/components/ui/button';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { useEffect, useState } from 'react';
import { useDrawer } from '../../contexts/DrawerContext';
import CashTendered, { cashProblem, changeFor } from '../order/CashTendered';

// Phase 1 external-PDQ: Verlo records the sale, the card machine (or the till drawer)
// takes the money, then the operator confirms it here. No gateway, no simulated success.
const BarPaymentDialog = ({
  open,
  onOpenChange,
  paymentStep,
  paymentMethod = 'card',
  onPaymentMethodChange,
  subtotal,
  vatAmount,
  customerName,
  cartLength,
  onTakePayment,
  onClearAndStartNew,
  formatCurrency,
}) => {
  const takeInstruction = paymentMethod === 'cash'
    ? 'Take the cash, then confirm below.'
    : 'Take the payment on the card machine, then confirm below.';
  // No drawer on this terminal → card only (the server refuses cash there too).
  const { hasDrawer } = useDrawer();
  // Cash: what was handed over, and the change (CashTendered).
  const [tendered, setTendered] = useState(null);
  const [change, setChange] = useState(0);
  useEffect(() => { if (open) { setTendered(null); setChange(0); } }, [open]);
  const cash = paymentMethod === 'cash';
  // Nothing handed over yet, less than the bill, or more change than any note (CashTendered).
  const short = cash && cashProblem(tendered, subtotal) !== 'ok';
  const take = () => {
    setChange(cash && tendered != null ? changeFor(tendered, subtotal) : 0);
    onTakePayment(cash ? tendered : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (newOpen) return;
      if (paymentStep === 'processing') return;   // no accidental close mid-record
      if (paymentStep === 'success') {
        onClearAndStartNew();
      } else {
        onOpenChange(false);
      }
    }}>
      <DialogContent size="form" data-testid="bar-payment-dialog">
        <DialogHeader className="text-center">
          <DialogTitle className="text-xl">
            {paymentStep === 'success' ? 'Paid' : 'Take payment'}
          </DialogTitle>
        </DialogHeader>
        <div className="py-6 text-center space-y-4">
          {paymentStep === 'waiting' && (
            <>
              <div>
                <p className="text-3xl font-bold">{formatCurrency(subtotal)}</p>
                {vatAmount != null && <p className="text-sm text-ink-soft mt-1">Includes VAT: {formatCurrency(vatAmount)}</p>}
              </div>

              <Segmented
                size="lg"
                stretch
                value={paymentMethod}
                onChange={(m) => onPaymentMethodChange?.(m)}
                options={[
                  { value: 'card', label: 'Card', testId: 'bar-payment-method-card' },
                  ...(hasDrawer ? [{ value: 'cash', label: 'Cash', testId: 'bar-payment-method-cash' }] : []),
                ]}
                data-testid="bar-payment-method-toggle"
              />
              {cash
                ? <CashTendered due={subtotal} value={tendered} onChange={setTendered} formatCurrency={formatCurrency} />
                : <p className="text-sm text-muted-foreground">{takeInstruction}</p>}
            </>
          )}
          {paymentStep === 'processing' && (
            <>
              <div className="w-20 h-20 mx-auto bg-signal-info-tint rounded-2xl flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-verlo border-t-transparent" />
              </div>
              <div>
                <p className="text-lg font-semibold">Recording payment…</p>
                <p className="text-muted-foreground mt-1">Please wait</p>
              </div>
            </>
          )}
          {paymentStep === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-signal-ok-tint rounded-2xl flex items-center justify-center">
                <svg className="w-10 h-10 text-signal-ok" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-xl font-bold text-signal-ok">Payment recorded</p>
                <p className="text-2xl font-bold mt-2">{formatCurrency(subtotal)}</p>
                {change > 0 && (
                  <div className="mt-3 flex items-center justify-between border border-signal-ok-line bg-signal-ok-tint px-4 py-3 text-signal-ok" data-testid="paid-change">
                    <span className="font-heading text-base font-bold">Give change</span>
                    <span className="font-heading text-3xl font-extrabold tabular-nums">{formatCurrency(change)}</span>
                  </div>
                )}
                <p className="text-muted-foreground mt-1">Bar Order{customerName ? ` - ${customerName}` : ''}</p>
              </div>
              <div className="bg-surface-2 rounded-lg p-3 text-left text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-ink-soft">Items</span>
                  <span>{cartLength}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1 border-t">
                  <span>Total Paid</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter className="flex-col gap-2">
          {paymentStep === 'waiting' && (
            <>
              <Button
                className="bg-verlo hover:bg-verlo-hover w-full"
                onClick={take}
                disabled={short}
                data-testid="confirm-bar-payment-btn"
              >
                {cash
                  ? (tendered == null ? 'Enter what they handed over'
                    : changeFor(tendered, subtotal) > 0 ? `Take ${formatCurrency(tendered)} · give ${formatCurrency(changeFor(tendered, subtotal))} change`
                      : `Take ${formatCurrency(subtotal)} in cash`)
                  : 'Confirm payment taken'}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
                Cancel
              </Button>
            </>
          )}
          {paymentStep === 'success' && (
            <Button
              className="bg-verlo hover:bg-verlo-hover w-full"
              onClick={onClearAndStartNew}
              data-testid="clear-start-new-btn"
            >
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BarPaymentDialog;
